import { useState, useEffect, useCallback, useRef } from 'react';

/*
  Rooks - useThrottle
  https://react-hooks.org/docs/useThrottle
  https://github.com/imbhargav5/rooks/blob/main/src/hooks/useThrottle.ts
*/

/**
 * useThrottle
 * Throttles a function with a timeout and ensures
 * that the callback function runs at most once in that duration
 *
 * @param function_ The callback to throttle
 * @param timeout Throttle timeout
 * @returns [throttledFunction, isReady]
 */
const useThrottle = (
  function_: (...args: unknown[]) => unknown,
  timeout = 300,
): [(...args: unknown[]) => unknown, boolean] => {
  const [ready, setReady] = useState(true);
  const timerRef = useRef<number | undefined>(undefined);

  if (!function_ || typeof function_ !== 'function') {
    throw new Error(
      'As a first argument, you need to pass a function to useThrottle hook.',
    );
  }

  const throttledFunction = useCallback(
    (...args: unknown[]) => {
      if (!ready) {
        return;
      }

      setReady(false);
      function_(...args);
    },
    [ready, function_],
  );

  useEffect(() => {
    if (typeof window !== 'undefined') {
      if (!ready) {
        timerRef.current = window.setTimeout(() => {
          setReady(true);
        }, timeout);

        return () => window.clearTimeout(timerRef.current);
      }
    } else {
      console.warn('useThrottle: window is undefined.');
    }
  }, [ready, timeout]);

  return [throttledFunction, ready];
};

export default useThrottle;
